// Copyright © 2026 Alex Bruce. All rights reserved.
//
// Parsing + division resolution for cost-code catalog imports.
//
// Kept pure (rows in, plan out) so it can be unit tested without touching
// files or Supabase. The caller reads the CSV/XLSX into row objects and
// applies the returned plan.

import { ALL_DIVISIONS } from "../projectData";

export interface ParsedCode { divId: string; costCode: string; name: string; }
export interface SkippedRow { row: number; reason: string; raw: string; }

export interface ImportPlan {
  /** Entries not currently in the catalog. */
  toAdd: ParsedCode[];
  /** Entries whose code exists but whose name differs. */
  toUpdate: (ParsedCode & { oldName: string })[];
  /** Entries already present and identical. */
  unchanged: ParsedCode[];
  /** Rows that couldn't be used, with a human-readable reason. */
  skipped: SkippedRow[];
}

export interface ExistingEntry { divId: string; costCode: string; name: string; }

const norm = (s: unknown) => String(s ?? "").trim();
const lower = (s: unknown) => norm(s).toLowerCase();

const COST_CODE_ALIASES = ["cost code", "costcode", "code", "cost_code", "number", "no", "#", "item code"];
const NAME_ALIASES      = ["name", "description", "subcategory", "sub category", "title", "item", "scope", "trade"];
const DIV_ALIASES       = ["division", "div", "csi", "csi division", "category", "group"];

/**
 * Find the header best matching a set of aliases.
 *
 * Alias order encodes priority, and every alias is tried against all headers
 * before moving on — iterating headers first would let a bare "code" column
 * win over "cost code" purely because it appeared earlier in the sheet.
 * Exact matches beat substring matches. Substring matching is limited to
 * aliases of 3+ characters so short ones like "no" don't swallow "notes".
 */
function findHeader(headers: string[], aliases: string[]): string | null {
  for (const a of aliases) {
    const exact = headers.find(h => lower(h) === a);
    if (exact) return exact;
  }
  for (const a of aliases) {
    if (a.length < 3) continue;
    const partial = headers.find(h => lower(h).includes(a));
    if (partial) return partial;
  }
  return null;
}

/**
 * Resolve a division from an explicit division cell and/or a cost code.
 *
 * Accepts the shapes people actually have in spreadsheets: "09", "9",
 * "Div 09", "div9", "Division 09", "Finishes". Falls back to the numeric
 * prefix of the cost code ("09-250" → div9), which is what makes files
 * with no division column work at all.
 */
export function resolveDivision(divCell: string, costCode: string): string | null {
  const cell = norm(divCell);

  if (cell) {
    // Any 1-2 digit run in the cell, e.g. "Div 09" / "09" / "Division 9"
    const numMatch = cell.match(/\d{1,2}/);
    if (numMatch) {
      const id = `div${parseInt(numMatch[0], 10)}`;
      if (ALL_DIVISIONS.some(d => d.id === id)) return id;
    }
    // Division name, e.g. "Finishes"
    const byName = ALL_DIVISIONS.find(d => lower(d.name) === lower(cell));
    if (byName) return byName.id;
  }

  // Cost-code prefix, e.g. "09-250" or "09250"
  const codeMatch = norm(costCode).match(/^\s*(\d{1,2})/);
  if (codeMatch) {
    const id = `div${parseInt(codeMatch[1], 10)}`;
    if (ALL_DIVISIONS.some(d => d.id === id)) return id;
  }

  return null;
}

/**
 * Turn raw spreadsheet rows into an apply-able plan.
 *
 * `rows` are header-keyed objects (lowercased headers), as produced by the
 * CSV/XLSX readers. `existing` is the org's current catalog.
 */
export function buildImportPlan(rows: Record<string, string>[], existing: ExistingEntry[]): ImportPlan {
  const plan: ImportPlan = { toAdd: [], toUpdate: [], unchanged: [], skipped: [] };
  if (rows.length === 0) return plan;

  const headers = Object.keys(rows[0]);
  const codeH = findHeader(headers, COST_CODE_ALIASES);
  const nameH = findHeader(headers, NAME_ALIASES);
  const divH  = findHeader(headers, DIV_ALIASES);

  if (!codeH || !nameH) {
    plan.skipped.push({
      row: 0,
      reason: `Couldn't find ${!codeH ? "a cost code" : "a name"} column. Expected a header like ${!codeH ? '"Cost Code"' : '"Name"'}.`,
      raw: headers.join(", "),
    });
    return plan;
  }

  const byKey = new Map(existing.map(e => [`${e.divId}|${lower(e.costCode)}`, e]));
  // Guards against a file that lists the same code twice.
  const seen = new Set<string>();

  rows.forEach((r, i) => {
    const rowNum = i + 2; // header is row 1
    const costCode = norm(r[codeH]);
    const name = norm(r[nameH]);
    const raw = [costCode, name].filter(Boolean).join(" — ") || "(blank)";

    if (!costCode && !name) return;                       // blank filler row
    if (!costCode) { plan.skipped.push({ row: rowNum, reason: "No cost code", raw }); return; }
    if (!name)     { plan.skipped.push({ row: rowNum, reason: "No name", raw }); return; }

    const divId = resolveDivision(divH ? r[divH] : "", costCode);
    if (!divId) {
      plan.skipped.push({ row: rowNum, reason: "Couldn't determine a CSI division", raw });
      return;
    }

    const key = `${divId}|${lower(costCode)}`;
    if (seen.has(key)) { plan.skipped.push({ row: rowNum, reason: "Duplicate of an earlier row", raw }); return; }
    seen.add(key);

    const hit = byKey.get(key);
    if (!hit)                    plan.toAdd.push({ divId, costCode, name });
    else if (hit.name !== name)  plan.toUpdate.push({ divId, costCode, name, oldName: hit.name });
    else                         plan.unchanged.push({ divId, costCode, name });
  });

  return plan;
}
